import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import Tabs from "../../common/ui/tabs"
import { RequireRole } from "../../common/validation"
import LoanOfficerDetails from "../../common/ui/loan-officer-details/details"
import LoanOfficerDetailsHeader from "../../common/ui/loan-officer-details/header"
import { Integration, ZillowReview } from "../../types"
import If from "../../common/ui/if"
import EditLoProfile from "../../common/ui/account/edit-lo-profile"
import IntegrationsTable from "../../common/ui/integrations/integrationTable"
import PricingLogs from "../../common/ui/pricing-logs"
import LoanOfficerRates from "../../common/ui/loan-officer-details/loan-officer-rates"
import {
  useFetchIntegrationsByLoIdQuery,
  useDeleteLoanOfficerMutation,
  useFetchLoanOfficerQuery,
  useFetchLoanOfficerReviewsQuery
} from "../../redux/queries"
import BBDialog from "../../common/ui/dialog"
import { pushToast } from "../../redux/slices/notifications"
import { useAppDispatch } from "../../common/hooks"
import LOActivities from "../../common/ui/loan-officer-details/activities"
import { LoanOfficerDetailsSkeleton } from "../../common/ui/skeletons"

const LoanOfficerDetailsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [deleteLoanOfficer, { isSuccess, isError }] =
    useDeleteLoanOfficerMutation()

  const { data: loDetails, isFetching } = useFetchLoanOfficerQuery(id ?? "", {
    skip: !id
  })
  const { data: reviews } = useFetchLoanOfficerReviewsQuery(id ?? "", {
    skip: !id
  })

  const { data: integrations, isFetching: isFetchingIntegrationsAll } =
    useFetchIntegrationsByLoIdQuery(id as string)

  const [tab, setTab] = useState("loan-officer-details")
  const [editLoanOfficerVisible, setEditLoanOfficerVisible] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const handleConfirmDelete = () => {
    if (pendingDeleteId) deleteLoanOfficer(pendingDeleteId)
    setPendingDeleteId(null)
  }

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        pushToast({
          theme: "success",
          msg: "Loan Officer deleted successfully",
          timeout: 3000
        })
      )
      navigate("/loan-officers", { replace: true })
    }
  }, [dispatch, isSuccess, navigate])

  useEffect(() => {
    if (isError) {
      dispatch(
        pushToast({
          theme: "danger",
          msg: "something went wrong",
          timeout: 3000
        })
      )
    }
  }, [dispatch, isError])

  if (!loDetails || !id)
    return (
      <If
        condition={!isFetching}
        then={<div className="p-6">Loan Officer not found</div>}
        else={<LoanOfficerDetailsSkeleton />}
      />
    )

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.brands}>
      <>
        <div className="h-full bg-white">
          <LoanOfficerDetailsHeader
            loanOfficer={loDetails}
            onClickDelete={setPendingDeleteId}
            onClickEdit={() => setEditLoanOfficerVisible(true)}
          />
          <Tabs
            className="px-6"
            currentTab={tab}
            tabs={[
              { id: "loan-officer-details", label: "Details" },
              { id: "integrations", label: "Integrations" },
              { id: "pricing-log", label: "Pricing Log" },
              { id: "rates", label: "Rates" },
              { id: "activity", label: "Activity" }
            ]}
            onChange={setTab}
          />

          <div>
            <If
              condition={tab === "loan-officer-details"}
              then={
                <LoanOfficerDetails
                  loanOfficer={loDetails}
                  review={(reviews?.zillow?.reviews ?? []) as ZillowReview[]}
                />
              }
            />
            <If
              condition={tab === "integrations" && integrations !== undefined}
              isLoading={isFetchingIntegrationsAll}
              then={
                <IntegrationsTable
                  data={integrations as Integration[]}
                  isLoading={isFetchingIntegrationsAll}
                />
              }
            />
            <If
              condition={tab === "pricing-log"}
              then={<PricingLogs id={{ loid: id }} />}
            />
            <If
              condition={tab === "rates"}
              then={<LoanOfficerRates id={id} />}
            />
            <If
              condition={tab === "activity"}
              then={<LOActivities loanOfficer={loDetails} />}
            />
          </div>
          <EditLoProfile
            data={loDetails}
            open={editLoanOfficerVisible}
            isInSidePanel
            onClose={() => setEditLoanOfficerVisible(false)}
          />
        </div>
        <BBDialog
          open={!!pendingDeleteId}
          title="Delete Loan Officer Profile"
          body="By doing this all the informaiton stored in the system about this loan officer will be deleted. Are you sure ?"
          okLabel="Yes, Delete"
          onOk={handleConfirmDelete}
          onCancel={() => setPendingDeleteId(null)}
          onClose={() => setPendingDeleteId(null)}
          cancelLabel="Cancel"
        />
      </>
    </RequireRole>
  )
}

export default LoanOfficerDetailsPage
